import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import { trackEvent } from "@shared/lib/posthog";

type ModalData = {
  currentPlanName: string;
  targetPlanName: string;
  targetJobLimit?: number;
  targetUserLimit?: number | string;
  publishedJobsCount?: number;
  activeUserCount?: number;
  isActiveSubscription: boolean;
  isLegacyUnlimitedJobsPlan: boolean;
  hasPendingInvites?: boolean;
};

type Props = {
  onCancel: () => void;
  modalData: ModalData;
  location: any;
  history: any;
};

function PlanChangeBlockedModal(props: Props) {
  const {
    modalData: {
      currentPlanName,
      targetPlanName,
      targetJobLimit,
      targetUserLimit,
      publishedJobsCount,
      activeUserCount,
      isActiveSubscription,
      isLegacyUnlimitedJobsPlan,
      hasPendingInvites,
    },
  } = props;

  // Determine blocking type - only flag as issue if current exceeds target
  const isJobLimitIssue =
    targetJobLimit !== undefined &&
    publishedJobsCount !== undefined &&
    publishedJobsCount > targetJobLimit;

  const isUserLimitIssue =
    targetUserLimit !== undefined &&
    activeUserCount !== undefined &&
    typeof targetUserLimit === "number" &&
    targetUserLimit !== 10000 && // Skip if unlimited (assuming 10000 = unlimited)
    activeUserCount > targetUserLimit;

  // Calculate excess amounts
  const jobsToArchive = isJobLimitIssue ? publishedJobsCount! - targetJobLimit! : 0;
  const usersToDeactivate = isUserLimitIssue ? activeUserCount! - (targetUserLimit as number) : 0;

  // Helper functions for cleaner message generation
  const getActionText = (verb: "downgrade" | "select") =>
    `${verb} ${verb === "downgrade" ? `from the ${currentPlanName} to` : "the"} ${targetPlanName}`;

  const getUserLimitText = () => `${targetUserLimit}`;

  const getUserCountText = () =>
    hasPendingInvites
      ? `${activeUserCount} active users (including pending invites)`
      : `${activeUserCount} active users`;

  // Determine modal title and action verbs
  const isDowngrade = isActiveSubscription && !isLegacyUnlimitedJobsPlan;
  const modalTitle = isDowngrade ? "Cannot downgrade plan" : "Cannot select plan";
  const actionVerb = isDowngrade ? "downgrade" : "select";
  const actioningVerb = isDowngrade ? "downgrading to" : "selecting";

  // Generate messages based on blocking type - prioritize jobs first for downgrades
  let primaryMessage: string;
  let secondaryMessage: string;
  let buttonText: string;
  let buttonAction: string;

  if (isJobLimitIssue && isUserLimitIssue) {
    // Both issues - show jobs first since they're typically easier to handle
    const jobText = (count: number) => `${count} published job${count === 1 ? "" : "s"}`;

    primaryMessage = `You cannot ${getActionText(actionVerb)} plan because you have ${jobText(
      publishedJobsCount!,
    )} and ${getUserCountText()}, but the ${targetPlanName} plan only allows ${jobText(
      targetJobLimit!,
    )} and ${getUserLimitText()} active users.`;

    secondaryMessage = `Please start by archiving ${jobsToArchive} job${
      jobsToArchive > 1 ? "s" : ""
    }, then you'll need to free up ${usersToDeactivate} active user seat${
      usersToDeactivate === 1 ? "" : "s"
    }.`;

    buttonText = "Manage jobs first";
    buttonAction = "/jobs/all";
  } else if (isJobLimitIssue) {
    const jobText = (count: number) => `${count} published job${count === 1 ? "" : "s"}`;

    primaryMessage = `You cannot ${getActionText(actionVerb)} plan because you have ${jobText(
      publishedJobsCount!,
    )}, but the ${targetPlanName} plan only allows ${jobText(targetJobLimit!)}.`;

    secondaryMessage = `Please archive ${jobsToArchive} job${
      jobsToArchive > 1 ? "s" : ""
    } before ${actioningVerb} the ${targetPlanName} plan.`;

    buttonText = "Manage jobs";
    buttonAction = "/jobs/all";
  } else if (isUserLimitIssue) {
    const userLimitText = getUserLimitText();
    const userCountText = getUserCountText();

    primaryMessage = `You cannot ${getActionText(
      actionVerb,
    )} plan because you have ${userCountText}, but the ${targetPlanName} plan only allows ${userLimitText} active users.`;

    // Determine what actions they can take based on their situation
    const canDeactivateUsers = activeUserCount! > 1; // Can't deactivate if only 1 user
    const seatsNeeded = usersToDeactivate;
    const seatText = seatsNeeded === 1 ? "seat" : "seats";

    let actionText = "";
    if (canDeactivateUsers && hasPendingInvites) {
      // They have both options available
      actionText = `free up ${seatsNeeded} active user ${seatText} by deactivating users and/or canceling pending invites`;
    } else if (canDeactivateUsers) {
      // Only users can be deactivated
      actionText = `deactivate ${seatsNeeded} user${
        seatsNeeded > 1 ? "s" : ""
      } to free up ${seatsNeeded} active user ${seatText}`;
    } else if (hasPendingInvites) {
      // Only pending invites can be canceled
      actionText = `cancel ${seatsNeeded} pending invite${
        seatsNeeded > 1 ? "s" : ""
      } to free up ${seatsNeeded} active user ${seatText}`;
    } else {
      // Fallback - shouldn't happen but just in case
      actionText = `reduce your active user count by ${seatsNeeded}`;
    }

    secondaryMessage = `Please ${actionText} before ${actioningVerb} the ${targetPlanName} plan.`;

    buttonText = "Manage team";
    buttonAction = "/hire/settings/team";
  } else {
    // Fallback case
    primaryMessage = `You cannot select the ${targetPlanName} plan due to current plan limits.`;
    secondaryMessage = "Please review your current usage and try again.";
    buttonText = "OK";
    buttonAction = "";
  }

  const handleButtonClick = () => {
    trackEvent("plan_change_blocked_action_clicked", { button_action: buttonAction, button_text: buttonText });
    if (buttonAction) {
      props.history.push(buttonAction);
    }
    props.onCancel();
  };

  return (
    <CenterModal headerTitleText={modalTitle} onCancel={props.onCancel}>
      <>
        <Styled.Instructions>{primaryMessage}</Styled.Instructions>
        <Styled.SecondaryMessage>{secondaryMessage}</Styled.SecondaryMessage>

        {isLegacyUnlimitedJobsPlan && (
          <Styled.SecondaryMessage>
            You are currently on a legacy plan.{" "}
            <b>If you change your plan, you will not be able to switch back to your legacy plan.</b>
          </Styled.SecondaryMessage>
        )}

        <Styled.ButtonContainer>
          <Button
            name={isJobLimitIssue ? "viewJobsButton" : "manageUsersButton"}
            onClick={handleButtonClick}
          >
            {buttonText}
          </Button>
          <Button styleType="secondary" onClick={props.onCancel}>
            Cancel
          </Button>
        </Styled.ButtonContainer>
      </>
    </CenterModal>
  );
}

export default PlanChangeBlockedModal;

/* Styled Components */
const Styled: any = {};

Styled.Instructions = styled.p<{ theme: any }>((props) => {
  const t = props.theme;
  return css`
    label: PlanChangeBlockedModal_Instructions;
    ${[t.mb(3)]}

    a {
      ${t.text.medium}
    }

    a:hover {
      text-decoration: underline;
    }
  `;
});

Styled.SecondaryMessage = styled.p<{ theme: any }>((props) => {
  const t = props.theme;
  return css`
    label: PlanChangeBlockedModal_SecondaryMessage;
    ${[t.mb(5), t.text.sm]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.ButtonContainer = styled.div<{ theme: any }>((props) => {
  const t = props.theme;
  return css`
    label: PlanChangeBlockedModal_ButtonContainer;
    display: flex;
    > * {
      ${t.mr(3)}

      &:last-of-type {
        ${t.mr(0)}
      }
    }
  `;
});
